import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { EnvelopeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { fetchUser } from "../../../api/users";
import { normalizeUserRecord } from "./userSetupStatus";
import { useEmailSignatureNagAudience } from "./useEmailSignatureNagAudience";

const DISMISS_PREFIX = "promang.emailSignatureGuide.dismissedOn";

function signatureMissing(user) {
  return !String(user?.email_signature_html || "").replace(/<[^>]+>/g, "").trim();
}

function localDayKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dismissStorageKey(userId) {
  return `${DISMISS_PREFIX}.${userId}`;
}

export function emailSignatureBannerDismissedToday(userId, date = new Date()) {
  if (!userId) return false;
  try {
    return window.localStorage.getItem(dismissStorageKey(userId)) === localDayKey(date);
  } catch {
    return false;
  }
}

function rememberEmailSignatureBannerDismiss(userId) {
  if (!userId) return;
  try {
    window.localStorage.setItem(dismissStorageKey(userId), localDayKey());
  } catch {
    /* private mode or full storage */
  }
}

export function EmailSignatureSetupBanner() {
  const { t } = useTranslation();
  const location = useLocation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const { eligible } = useEmailSignatureNagAudience();
  const [user, setUser] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const hiddenUntilTomorrow =
    dismissed || emailSignatureBannerDismissedToday(currentUser?.id);

  useEffect(() => {
    if (!currentUser?.id || !eligible) {
      setUser(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchUser(currentUser.id, { fields: "id,email_signature_html" });
        if (cancelled) return;
        const normalized = normalizeUserRecord(res) ?? res;
        const ok = normalized && normalized.id != null && !normalized.errors;
        setUser(ok ? normalized : null);
      } catch {
        if (!cancelled) setUser(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, eligible, location.pathname]);

  if (!eligible || hiddenUntilTomorrow || !user || !signatureMissing(user)) return null;
  if (location.pathname?.startsWith("/dashboard/user/profile")) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 left-0 right-0 z-[90] flex justify-center px-4">
      <div className="pointer-events-auto flex max-w-xl items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-lg">
        <EnvelopeIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-800" />
        <div className="min-w-0 text-sm">
          <div className="font-semibold text-amber-950">{t("case.mail_thread.signature_banner_title")}</div>
          <p className="mt-0.5 text-amber-900">{t("case.mail_thread.signature_banner_body")}</p>
          <Link
            to="/dashboard/user/profile#email"
            className="mt-1 inline-block font-semibold text-[#0B3A78] underline"
          >
            {t("case.mail_thread.signature_banner_cta")}
          </Link>
        </div>
        <button
          type="button"
          className="shrink-0 rounded-md p-1 text-amber-800 hover:bg-amber-100"
          onClick={() => {
            rememberEmailSignatureBannerDismiss(currentUser?.id);
            setDismissed(true);
          }}
          aria-label={t("common.close")}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
